/** Study-guide quiz banks for the Multivariable Integration on Moving Domains module — 20 MCQs per topic (6 Easy / 8 Medium / 6 Hard). */

// Coarea Formula and Level-Set Integration
export const MD_COAREA_QUIZ = [
  {
    prompt: "For $u\\in C^1(\\Omega)$ with $\\nabla u\\neq\\mathbf 0$ on a region $\\Omega\\subseteq\\mathbb R^2$ and $f$ continuous, the coarea formula states that $\\displaystyle\\iint_\\Omega f\\,|\\nabla u|\\,dA$ equals",
    options: [
      "$\\displaystyle\\int_{-\\infty}^{\\infty}\\Bigl(\\int_{\\{u=t\\}\\cap\\Omega}f\\,ds\\Bigr)dt$",
      "$\\displaystyle\\int_{-\\infty}^{\\infty}\\Bigl(\\int_{\\{u=t\\}\\cap\\Omega}\\frac{f}{|\\nabla u|}\\,ds\\Bigr)dt$",
      "$\\displaystyle\\int_{-\\infty}^{\\infty}f(t)\\,dt$",
      "$\\displaystyle\\int_{\\{u=0\\}\\cap\\Omega}f\\,ds$",
    ],
    answer: "A",
    explanation: "The gradient weight on the left is exactly what turns the area integral into an integral of level-curve integrals with plain arc length $ds$. The second option is the form for $\\iint_\\Omega f\\,dA$ (without the weight).",
    id: "md-coarea-01",
    difficulty: "Easy",
  },
  {
    prompt: "With $u(x,y)=x$, so that the level sets are vertical lines, the coarea formula reduces to",
    options: [
      "Green's theorem",
      "Stokes' theorem",
      "Fubini's theorem for iterated integrals",
      "The divergence theorem",
    ],
    answer: "C",
    explanation: "$\\nabla u=(1,0)$ has length $1$, and $\\{u=t\\}$ is the line $x=t$. The formula becomes $\\iint_\\Omega f\\,dA=\\int\\bigl(\\int f(t,y)\\,dy\\bigr)dt$, which is Fubini's theorem.",
    id: "md-coarea-02",
    difficulty: "Easy",
  },
  {
    prompt: "For $u(x,y)=\\sqrt{x^2+y^2}$ away from the origin, $|\\nabla u|$ equals",
    options: [
      "$r$",
      "$2r$",
      "$1/r$",
      "$1$",
    ],
    answer: "D",
    explanation: "$\\nabla u=(x,y)/r$, a unit vector. Because the weight is $1$, coarea with $u=r$ gives polar coordinates: $\\iint f\\,dA=\\int_0^\\infty\\bigl(\\int_{r=t}f\\,ds\\bigr)dt$.",
    id: "md-coarea-03",
    difficulty: "Easy",
  },
  {
    prompt: "At a point of a level curve $\\{u=t\\}$ where $\\nabla u\\neq\\mathbf 0$, the gradient $\\nabla u$ is",
    options: [
      "Tangent to the level curve",
      "Perpendicular to the level curve",
      "Always the zero vector",
      "Parallel to the $x$-axis",
    ],
    answer: "B",
    explanation: "If $\\boldsymbol\\gamma(s)$ runs along $\\{u=t\\}$ then $u(\\boldsymbol\\gamma(s))=t$, so $\\nabla u\\cdot\\boldsymbol\\gamma'(s)=0$. The unit normal to the level curve is $\\mathbf n=\\nabla u/|\\nabla u|$.",
    id: "md-coarea-04",
    difficulty: "Easy",
  },
  {
    prompt: "Taking $f\\equiv1$ in the coarea formula, $\\displaystyle\\iint_\\Omega|\\nabla u|\\,dA=\\int L(t)\\,dt$, where $L(t)$ is",
    options: [
      "The area of $\\{u<t\\}\\cap\\Omega$",
      "The value $u(t)$",
      "The length of the level curve $\\{u=t\\}\\cap\\Omega$",
      "The maximum of $|\\nabla u|$ on $\\{u=t\\}$",
    ],
    answer: "C",
    explanation: "With $f=1$ the inner integral is $\\int_{\\{u=t\\}\\cap\\Omega}ds$, the length of the level curve. So the total variation $\\iint|\\nabla u|\\,dA$ adds up level-curve lengths.",
    id: "md-coarea-05",
    difficulty: "Easy",
  },
  {
    prompt: "The form $\\displaystyle\\iint_\\Omega g\\,dA=\\int\\Bigl(\\int_{\\{u=t\\}}\\frac{g}{|\\nabla u|}\\,ds\\Bigr)dt$ requires",
    options: [
      "$\\nabla u\\neq\\mathbf 0$ on $\\Omega$",
      "$u$ to be linear",
      "$g$ to be constant",
      "$\\Omega$ to be a disk",
    ],
    answer: "A",
    explanation: "It comes from the weighted form with $f=g/|\\nabla u|$, which only makes sense where $|\\nabla u|>0$. Where $\\nabla u$ vanishes on a region, the level sets stop being curves and that region is lost (see the plateau counterexample).",
    id: "md-coarea-06",
    difficulty: "Easy",
  },
  {
    prompt: "For $u=x^2+y^2$ and $t>0$, the weighted length $\\displaystyle\\int_{\\{u=t\\}}\\frac{ds}{|\\nabla u|}$ equals",
    options: [
      "$2\\pi\\sqrt t$",
      "$\\pi$",
      "$\\pi/\\sqrt t$",
      "$2\\pi$",
    ],
    answer: "B",
    explanation: "The level set is the circle of radius $\\sqrt t$, of length $2\\pi\\sqrt t$, and $|\\nabla u|=2r=2\\sqrt t$ on it. The ratio is $\\pi$, matching $\\tfrac{d}{dt}(\\pi t)$, the rate of growth of the disk's area.",
    id: "md-coarea-07",
    difficulty: "Medium",
  },
  {
    prompt: "Using level sets of $u=x^2+y^2$, the integral $\\displaystyle\\iint_{\\mathbb R^2}e^{-(x^2+y^2)}\\,dA$ equals",
    options: [
      "$\\sqrt\\pi$",
      "$2\\pi$",
      "$\\pi/2$",
      "$\\pi$",
    ],
    answer: "D",
    explanation: "$\\iint e^{-u}\\,dA=\\int_0^\\infty e^{-t}\\Bigl(\\int_{u=t}\\frac{ds}{|\\nabla u|}\\Bigr)dt=\\int_0^\\infty e^{-t}\\,\\pi\\,dt=\\pi$.",
    id: "md-coarea-08",
    difficulty: "Medium",
  },
  {
    prompt: "$(X,Y)$ is uniform on the unit square $[0,1]^2$. Using $u=x+y$, the density of $U=X+Y$ at $t=\\tfrac12$ is",
    options: [
      "$\\tfrac12$",
      "$\\tfrac{\\sqrt2}{2}$",
      "$1$",
      "$\\tfrac14$",
    ],
    answer: "A",
    explanation: "The density is $\\int_{\\{u=t\\}}\\frac{ds}{|\\nabla u|}$. For $0\\le t\\le1$ the level segment has length $\\sqrt2\\,t$ and $|\\nabla u|=\\sqrt2$, giving density $t$. At $t=\\tfrac12$ that is $\\tfrac12$.",
    id: "md-coarea-09",
    difficulty: "Medium",
  },
  {
    prompt: "Let $V(t)$ be the area of $\\{u<t\\}$, with $\\nabla u\\neq\\mathbf 0$ on $\\{u=t\\}$. Then $V'(t)$ equals",
    options: [
      "The length of $\\{u=t\\}$",
      "$\\displaystyle\\int_{\\{u=t\\}}|\\nabla u|\\,ds$",
      "$\\displaystyle\\int_{\\{u=t\\}}\\frac{ds}{|\\nabla u|}$",
      "$0$",
    ],
    answer: "C",
    explanation: "$V(t)=\\int_{-\\infty}^{t}\\Bigl(\\int_{u=\\tau}\\frac{ds}{|\\nabla u|}\\Bigr)d\\tau$, so differentiating gives the weighted length. Plain length overcounts where the levels are tightly packed (large $|\\nabla u|$).",
    id: "md-coarea-10",
    difficulty: "Medium",
  },
  {
    prompt: "In $\\mathbb R^3$ with $u=|\\mathbf x|$ (so $|\\nabla u|=1$), coarea gives $\\displaystyle\\iiint f\\,dV=\\int_0^\\infty\\Bigl(\\iint_{|\\mathbf x|=r}f\\,dS\\Bigr)dr$. The volume of the ball of radius $R$ is therefore",
    options: [
      "$\\displaystyle\\int_0^R 2\\pi r\\,dr=\\pi R^2$",
      "$\\displaystyle\\int_0^R 4\\pi r^2\\,dr=\\tfrac43\\pi R^3$",
      "$4\\pi R^2$",
      "$\\displaystyle\\int_0^R 4\\pi r\\,dr=2\\pi R^2$",
    ],
    answer: "B",
    explanation: "With $f=1$ the inner integral is the area $4\\pi r^2$ of the sphere of radius $r$. Integrating gives $\\tfrac43\\pi R^3$; differentiating the volume gives back the surface area.",
    id: "md-coarea-11",
    difficulty: "Medium",
  },
  {
    prompt: "For $u=x^2+y^2$ on the unit disk $D$, $\\displaystyle\\iint_D|\\nabla u|\\,dA$ equals",
    options: [
      "$2\\pi/3$",
      "$\\pi$",
      "$2\\pi$",
      "$4\\pi/3$",
    ],
    answer: "D",
    explanation: "By coarea it is $\\int_0^1 L(t)\\,dt=\\int_0^1 2\\pi\\sqrt t\\,dt=\\tfrac{4\\pi}{3}$. Directly: $\\int_0^{2\\pi}\\!\\int_0^1 2r\\cdot r\\,dr\\,d\\theta=\\tfrac{4\\pi}{3}$.",
    id: "md-coarea-12",
    difficulty: "Medium",
  },
  {
    prompt: "Slicing a region $\\Omega$ along the lines $2x+y=t$, the area integral $\\displaystyle\\iint_\\Omega g\\,dA$ equals",
    options: [
      "$\\displaystyle\\sqrt5\\int\\Bigl(\\int_{\\{2x+y=t\\}}g\\,ds\\Bigr)dt$",
      "$\\displaystyle\\int\\Bigl(\\int_{\\{2x+y=t\\}}g\\,ds\\Bigr)dt$",
      "$\\displaystyle\\frac{1}{\\sqrt5}\\int\\Bigl(\\int_{\\{2x+y=t\\}}g\\,ds\\Bigr)dt$",
      "$\\displaystyle\\frac15\\int\\Bigl(\\int_{\\{2x+y=t\\}}g\\,ds\\Bigr)dt$",
    ],
    answer: "C",
    explanation: "$|\\nabla u|=|(2,1)|=\\sqrt5$ is constant, so the $1/|\\nabla u|$ factor comes out as $1/\\sqrt5$. Consecutive lines $t$ and $t+dt$ are only $dt/\\sqrt5$ apart.",
    id: "md-coarea-13",
    difficulty: "Medium",
  },
  {
    prompt: "For $u=\\dfrac{x^2}{a^2}+\\dfrac{y^2}{b^2}$ and $t>0$, the weighted length $\\displaystyle\\int_{\\{u=t\\}}\\frac{ds}{|\\nabla u|}$ equals",
    options: [
      "$\\pi ab$",
      "$2\\pi\\sqrt{abt}$",
      "$\\pi(a+b)$",
      "$\\pi ab\\sqrt t$",
    ],
    answer: "A",
    explanation: "It is the derivative of the area $\\pi ab\\,t$ of $\\{u<t\\}$, so it equals $\\pi ab$ for every $t$. The perimeter of the ellipse is an elliptic integral, but the gradient weighting makes the total elementary.",
    id: "md-coarea-14",
    difficulty: "Medium",
  },
  {
    prompt: "$u$ is a smooth radial function on the unit disk with $u=0$ for $r\\le\\tfrac12$ and $\\nabla u\\neq\\mathbf 0$ for $\\tfrac12<r<1$. The value of $\\displaystyle\\int_{t>0}\\Bigl(\\int_{\\{u=t\\}}\\frac{ds}{|\\nabla u|}\\Bigr)dt$ is",
    options: [
      "$\\pi$, the area of the disk",
      "$3\\pi/4$, the area of the annulus only",
      "$\\pi/4$, the area of the plateau",
      "It is infinite because $|\\nabla u|\\to0$",
    ],
    answer: "B",
    explanation: "The level curves for $t>0$ only sweep out the annulus, where the formula holds, giving $\\pi-\\tfrac{\\pi}{4}=\\tfrac{3\\pi}{4}$. The plateau, where $\\nabla u=\\mathbf 0$, is invisible to them. This is why the $1/|\\nabla u|$ form needs $\\nabla u\\neq\\mathbf 0$.",
    id: "md-coarea-15",
    difficulty: "Hard",
  },
  {
    prompt: "Why does the factor $1/|\\nabla u|$ appear in $dA=\\dfrac{ds\\,dt}{|\\nabla u|}$?",
    options: [
      "Because the level curves are always circles",
      "Because $|\\nabla u|$ is the length of the level curve",
      "Because $\\nabla u$ is tangent to the level curves",
      "Because the normal distance between the levels $t$ and $t+dt$ is $dt/|\\nabla u|$",
    ],
    answer: "D",
    explanation: "Moving a distance $dn$ along $\\mathbf n=\\nabla u/|\\nabla u|$ changes $u$ by $|\\nabla u|\\,dn$. So the strip between two nearby level curves has width $dt/|\\nabla u|$ and area $ds\\cdot dt/|\\nabla u|$.",
    id: "md-coarea-16",
    difficulty: "Hard",
  },
  {
    prompt: "In the derivation, $\\mathbf x(t,s)$ lies on $\\{u=t\\}$ and $s$ is arc length along that curve. Then $\\mathbf x_t\\cdot\\nabla u$ equals",
    options: [
      "$1$",
      "$0$",
      "$|\\nabla u|$",
      "$1/|\\nabla u|$",
    ],
    answer: "A",
    explanation: "Differentiate $u(\\mathbf x(t,s))=t$ in $t$: $\\nabla u\\cdot\\mathbf x_t=1$. Hence $\\mathbf x_t\\cdot\\mathbf n=1/|\\nabla u|$, and the Jacobian is $|\\det(\\mathbf x_t,\\mathbf x_s)|=(\\mathbf x_t\\cdot\\mathbf n)\\,|\\mathbf x_s|=1/|\\nabla u|$.",
    id: "md-coarea-17",
    difficulty: "Hard",
  },
  {
    prompt: "$(X,Y)$ is uniform on $[0,1]^2$. Using the level-set density of $U=X+Y$, $P\\bigl(U\\le\\tfrac12\\bigr)$ equals",
    options: [
      "$\\tfrac14$",
      "$\\tfrac12$",
      "$\\tfrac18$",
      "$\\tfrac1{16}$",
    ],
    answer: "C",
    explanation: "The density is $t$ on $[0,1]$, so $P(U\\le\\tfrac12)=\\int_0^{1/2}t\\,dt=\\tfrac18$. Check: $\\{x+y\\le\\tfrac12\\}$ is a right triangle with legs $\\tfrac12$, of area $\\tfrac18$.",
    id: "md-coarea-18",
    difficulty: "Hard",
  },
  {
    prompt: "With $u=x^2+y^2$, $\\dfrac{d}{dt}\\displaystyle\\iint_{\\{u<t\\}}(x^2+y^2)\\,dA$ for $t>0$ equals",
    options: [
      "$\\pi t^2/2$",
      "$\\pi t$",
      "$2\\pi t$",
      "$\\pi\\sqrt t$",
    ],
    answer: "B",
    explanation: "$\\tfrac{d}{dt}\\iint_{u<t}g\\,dA=\\int_{u=t}\\frac{g}{|\\nabla u|}\\,ds=t\\cdot\\pi$. Check: the integral is $2\\pi\\int_0^{\\sqrt t}r^3\\,dr=\\pi t^2/2$, whose derivative is $\\pi t$.",
    id: "md-coarea-19",
    difficulty: "Hard",
  },
  {
    prompt: "In $\\mathbb R^n$, with $u=|\\mathbf x|$ and the sphere of radius $r$ having area $\\omega_{n-1}r^{n-1}$, the integral of a radial function $f(\\mathbf x)=\\varphi(|\\mathbf x|)$ is",
    options: [
      "$\\displaystyle\\int_0^\\infty\\varphi(r)\\,dr$",
      "$\\displaystyle\\omega_{n-1}\\int_0^\\infty\\varphi(r)\\,r\\,dr$",
      "$\\displaystyle\\int_0^\\infty\\varphi(r)\\,r^{n}\\,dr$",
      "$\\displaystyle\\omega_{n-1}\\int_0^\\infty\\varphi(r)\\,r^{n-1}\\,dr$",
    ],
    answer: "D",
    explanation: "$|\\nabla u|=1$ and $f=\\varphi(r)$ is constant on each sphere, so the inner integral is $\\varphi(r)\\,\\omega_{n-1}r^{n-1}$. For $n=2$, $\\omega_1=2\\pi$ gives the familiar $2\\pi\\int\\varphi(r)\\,r\\,dr$.",
    id: "md-coarea-20",
    difficulty: "Hard",
  },
];

// Reynolds Transport Theorem
export const MD_REYNOLDS_QUIZ = [
  {
    prompt: "For $F(t)=\\displaystyle\\int_{a(t)}^{b(t)}f(x,t)\\,dx$ with smooth $a,b,f$, the Leibniz rule gives $F'(t)=$",
    options: [
      "$\\displaystyle\\int_{a}^{b}f_t\\,dx$",
      "$f(b,t)\\,b'(t)-f(a,t)\\,a'(t)$",
      "$\\displaystyle\\int_{a}^{b}f_t\\,dx+f(b,t)\\,b'(t)-f(a,t)\\,a'(t)$",
      "$\\displaystyle\\int_{a}^{b}f_t\\,dx+f(b,t)-f(a,t)$",
    ],
    answer: "C",
    explanation: "The integrand changes (first term) and the endpoints move (boundary terms). The Reynolds Transport Theorem is the multivariable version of exactly this rule.",
    id: "md-reynolds-01",
    difficulty: "Easy",
  },
  {
    prompt: "In the Reynolds Transport Theorem $\\dfrac{d}{dt}\\displaystyle\\iiint_{\\Omega_t}f\\,dV=\\iiint_{\\Omega_t}\\frac{\\partial f}{\\partial t}\\,dV+\\iint_{\\partial\\Omega_t}f\\,(\\mathbf v\\cdot\\mathbf n)\\,dS$, the quantity $\\mathbf v\\cdot\\mathbf n$ is",
    options: [
      "The normal speed of the boundary",
      "The divergence of $f$",
      "The curvature of the boundary",
      "The time derivative of $f$",
    ],
    answer: "A",
    explanation: "$\\mathbf v$ is the velocity of the boundary points and $\\mathbf n$ the outward unit normal. Only the normal component changes the region; sliding along the boundary does not.",
    id: "md-reynolds-02",
    difficulty: "Easy",
  },
  {
    prompt: "If the region $\\Omega$ does not move (a fixed control volume), $\\dfrac{d}{dt}\\displaystyle\\iiint_{\\Omega}f\\,dV$ equals",
    options: [
      "$0$",
      "$\\displaystyle\\iint_{\\partial\\Omega}f\\,dS$",
      "$\\displaystyle\\iiint_{\\Omega}\\nabla\\cdot(f\\mathbf v)\\,dV$",
      "$\\displaystyle\\iiint_{\\Omega}\\frac{\\partial f}{\\partial t}\\,dV$",
    ],
    answer: "D",
    explanation: "With $\\mathbf v\\cdot\\mathbf n=0$ on the boundary the flux term vanishes; only the change of the integrand remains.",
    id: "md-reynolds-03",
    difficulty: "Easy",
  },
  {
    prompt: "Taking $f\\equiv1$, Reynolds' theorem says the rate of change of the volume of $\\Omega_t$ is",
    options: [
      "$\\displaystyle\\iint_{\\partial\\Omega_t}\\mathbf v\\cdot\\mathbf n\\,dS$",
      "$0$ always",
      "The surface area of $\\partial\\Omega_t$",
      "$\\displaystyle\\iiint_{\\Omega_t}|\\mathbf v|\\,dV$",
    ],
    answer: "A",
    explanation: "$\\partial_t1=0$, so $V'(t)=\\iint_{\\partial\\Omega_t}\\mathbf v\\cdot\\mathbf n\\,dS$: the boundary sweeps out volume at the rate of its normal speed.",
    id: "md-reynolds-04",
    difficulty: "Easy",
  },
  {
    prompt: "A material volume is a region that",
    options: [
      "Is fixed in space",
      "Has constant volume",
      "Moves with the fluid, always containing the same fluid particles",
      "Is always a ball",
    ],
    answer: "C",
    explanation: "Its boundary moves with the fluid velocity $\\mathbf u$, so no fluid crosses it. A fixed region is a control volume instead.",
    id: "md-reynolds-05",
    difficulty: "Easy",
  },
  {
    prompt: "For a region moving with the flow of a velocity field $\\mathbf u$, the volume is constant for every such region exactly when",
    options: [
      "$\\nabla\\times\\mathbf u=\\mathbf 0$",
      "$\\nabla\\cdot\\mathbf u=0$",
      "$\\mathbf u$ is constant",
      "$|\\mathbf u|=1$",
    ],
    answer: "B",
    explanation: "By Reynolds and the divergence theorem, $V'(t)=\\iint\\mathbf u\\cdot\\mathbf n\\,dS=\\iiint\\nabla\\cdot\\mathbf u\\,dV$. This vanishes for all regions iff $\\nabla\\cdot\\mathbf u=0$: the flow is incompressible.",
    id: "md-reynolds-06",
    difficulty: "Easy",
  },
  {
    prompt: "A disk of radius $R(t)$ centred at the origin, with $f=x^2+y^2$. Reynolds' theorem gives $\\dfrac{d}{dt}\\displaystyle\\iint_{D_t}f\\,dA=$",
    options: [
      "$\\pi R^3R'$",
      "$2\\pi R^3$",
      "$2\\pi R^3R'$",
      "$\\tfrac{\\pi}{2}R^4$",
    ],
    answer: "C",
    explanation: "$\\partial_tf=0$; on the boundary $f=R^2$, $\\mathbf v\\cdot\\mathbf n=R'$ and the length is $2\\pi R$, so the rate is $R^2\\cdot R'\\cdot2\\pi R$. Check: $\\iint f\\,dA=\\tfrac{\\pi}{2}R^4$ has derivative $2\\pi R^3R'$.",
    id: "md-reynolds-07",
    difficulty: "Medium",
  },
  {
    prompt: "For a material volume in a flow with velocity $\\mathbf u$, Reynolds' theorem combined with the divergence theorem gives $\\dfrac{d}{dt}\\displaystyle\\iiint_{\\Omega_t}f\\,dV=$",
    options: [
      "$\\displaystyle\\iiint_{\\Omega_t}\\Bigl(\\frac{\\partial f}{\\partial t}+\\nabla\\cdot(f\\mathbf u)\\Bigr)dV$",
      "$\\displaystyle\\iiint_{\\Omega_t}\\frac{\\partial f}{\\partial t}\\,dV$",
      "$\\displaystyle\\iiint_{\\Omega_t}\\nabla\\cdot(f\\mathbf u)\\,dV$",
      "$\\displaystyle\\iiint_{\\Omega_t}\\Bigl(\\frac{\\partial f}{\\partial t}+\\nabla\\times(f\\mathbf u)\\Bigr)dV$",
    ],
    answer: "A",
    explanation: "The boundary term $\\iint f\\,\\mathbf u\\cdot\\mathbf n\\,dS$ becomes $\\iiint\\nabla\\cdot(f\\mathbf u)\\,dV$. Curl plays no role.",
    id: "md-reynolds-08",
    difficulty: "Medium",
  },
  {
    prompt: "Mass $\\displaystyle\\iiint_{\\Omega_t}\\rho\\,dV$ in every material volume is conserved. Since the volume is arbitrary, $\\rho$ must satisfy",
    options: [
      "$\\nabla\\cdot\\mathbf u=0$",
      "$\\dfrac{\\partial\\rho}{\\partial t}=0$",
      "$\\nabla\\times(\\rho\\mathbf u)=\\mathbf 0$",
      "$\\dfrac{\\partial\\rho}{\\partial t}+\\nabla\\cdot(\\rho\\mathbf u)=0$",
    ],
    answer: "D",
    explanation: "$0=\\iiint(\\rho_t+\\nabla\\cdot(\\rho\\mathbf u))\\,dV$ for every region forces the integrand to vanish: the continuity equation.",
    id: "md-reynolds-09",
    difficulty: "Medium",
  },
  {
    prompt: "The interval $[0,b(t)]$ with $b(t)=t^2$ and $f(x,t)=tx$. Then $\\dfrac{d}{dt}\\displaystyle\\int_0^{b(t)}f\\,dx$ at $t=1$ equals",
    options: [
      "$\\tfrac12$",
      "$\\tfrac52$",
      "$2$",
      "$\\tfrac32$",
    ],
    answer: "B",
    explanation: "$\\int_0^{1}x\\,dx+f(1,1)\\cdot b'(1)=\\tfrac12+1\\cdot2=\\tfrac52$. Check: $\\int_0^{t^2}tx\\,dx=\\tfrac{t^5}{2}$, derivative $\\tfrac52t^4=\\tfrac52$ at $t=1$.",
    id: "md-reynolds-10",
    difficulty: "Medium",
  },
  {
    prompt: "A square $[0,s(t)]^2$ with $f\\equiv1$. Only the edges $x=s$ and $y=s$ move, each with normal speed $s'$. The rate of change of area from Reynolds' theorem is",
    options: [
      "$4ss'$",
      "$s'$",
      "$2ss'$",
      "$s^2s'$",
    ],
    answer: "C",
    explanation: "Two moving edges of length $s$ with normal speed $s'$ give $2ss'$; the fixed edges contribute $0$. This matches $\\tfrac{d}{dt}s^2$.",
    id: "md-reynolds-11",
    difficulty: "Medium",
  },
  {
    prompt: "In a fixed control volume, the boundary term in $\\dfrac{d}{dt}\\displaystyle\\iiint\\rho\\,dV=-\\iint_{\\partial\\Omega}\\rho\\,\\mathbf u\\cdot\\mathbf n\\,dS$ represents",
    options: [
      "Net mass flowing out through the boundary per unit time, with a minus sign",
      "The motion of the boundary",
      "The rotation of the fluid",
      "The mass created inside the region",
    ],
    answer: "A",
    explanation: "The boundary is fixed, but fluid crosses it. Mass inside decreases at the rate it flows out. The flux term now comes from the fluid velocity $\\mathbf u$, not the boundary velocity.",
    id: "md-reynolds-12",
    difficulty: "Medium",
  },
  {
    prompt: "A ball of radius $R(t)$ in $\\mathbb R^3$ contains a uniform density $\\rho(t)$. Reynolds' theorem gives $\\dfrac{dM}{dt}=$",
    options: [
      "$\\tfrac43\\pi R^3\\rho'$",
      "$4\\pi R^2\\rho R'$",
      "$\\tfrac43\\pi R^3\\rho'+\\tfrac43\\pi R^3\\rho R'$",
      "$\\tfrac43\\pi R^3\\rho'+4\\pi R^2\\rho R'$",
    ],
    answer: "D",
    explanation: "The volume term is $\\iiint\\rho'\\,dV=\\tfrac43\\pi R^3\\rho'$ and the boundary term is $\\rho\\,R'\\cdot4\\pi R^2$. Together they are the product rule for $\\tfrac43\\pi R^3\\rho$.",
    id: "md-reynolds-13",
    difficulty: "Medium",
  },
  {
    prompt: "A region translates rigidly with constant velocity $\\mathbf c$, and $f=f(\\mathbf x)$ does not depend on $t$. Then $\\dfrac{d}{dt}\\displaystyle\\iiint_{\\Omega_t}f\\,dV$ equals",
    options: [
      "$0$ always",
      "$\\displaystyle\\iiint_{\\Omega_t}\\mathbf c\\cdot\\nabla f\\,dV$",
      "$\\displaystyle|\\mathbf c|\\iint_{\\partial\\Omega_t}f\\,dS$",
      "$\\displaystyle\\iiint_{\\Omega_t}f\\,dV$",
    ],
    answer: "B",
    explanation: "The boundary term is $\\iint f\\,\\mathbf c\\cdot\\mathbf n\\,dS=\\iiint\\nabla\\cdot(f\\mathbf c)\\,dV=\\iiint\\mathbf c\\cdot\\nabla f\\,dV$. It is zero only when $f$ is constant along $\\mathbf c$.",
    id: "md-reynolds-14",
    difficulty: "Medium",
  },
  {
    prompt: "In 1D, the continuity equation $\\rho_t+(\\rho u)_x=0$ holds with $u=x$ and $\\rho(x,0)=1$. Along the material interval $[a(t),b(t)]$ with $a'=a$, $b'=b$, the mass is",
    options: [
      "Increasing like $e^t$",
      "Decreasing like $e^{-t}$",
      "Constant",
      "Zero",
    ],
    answer: "C",
    explanation: "Reynolds gives $M'=\\int_a^b\\rho_t\\,dx+\\rho(b)b'-\\rho(a)a'=\\int_a^b(\\rho_t+(\\rho u)_x)\\,dx=0$. The interval stretches like $e^t$ while $\\rho=e^{-t}$ thins, and the mass stays the same.",
    id: "md-reynolds-15",
    difficulty: "Hard",
  },
  {
    prompt: "Why does only the normal component $\\mathbf v\\cdot\\mathbf n$ of the boundary velocity appear in Reynolds' theorem?",
    options: [
      "Because the tangential component is always zero",
      "Because $\\mathbf v$ is always parallel to $\\mathbf n$",
      "Because the tangential component is cancelled by the volume term",
      "Because moving a boundary point along the boundary does not change the region",
    ],
    answer: "D",
    explanation: "A boundary element $dS$ moving with velocity $\\mathbf v$ sweeps out volume $(\\mathbf v\\cdot\\mathbf n)\\,dS\\,dt$ in time $dt$. Sliding along the surface sweeps out nothing. A velocity field may well have tangential parts.",
    id: "md-reynolds-16",
    difficulty: "Hard",
  },
  {
    prompt: "A balloon's radius is $R(t)=1+t$ and the uniform density of the gas inside is $\\rho(t)=\\dfrac{1}{(1+t)^3}$. The mass is constant because",
    options: [
      "The volume term $-\\tfrac{4\\pi}{1+t}$ exactly cancels the boundary term $+\\tfrac{4\\pi}{1+t}$",
      "Both terms are zero",
      "The boundary is not moving",
      "$\\nabla\\cdot\\mathbf u=0$ inside the balloon",
    ],
    answer: "A",
    explanation: "Volume term: $\\tfrac43\\pi R^3\\rho'=\\tfrac43\\pi(1+t)^3\\cdot\\bigl(-3(1+t)^{-4}\\bigr)=-\\tfrac{4\\pi}{1+t}$. Boundary term: $\\rho\\,R'\\cdot4\\pi R^2=(1+t)^{-3}\\cdot4\\pi(1+t)^2=\\tfrac{4\\pi}{1+t}$. The gas expands, so the divergence is not zero.",
    id: "md-reynolds-17",
    difficulty: "Hard",
  },
  {
    prompt: "For sublevel sets $\\Omega_t=\\{u<t\\}$ with $\\nabla u\\neq\\mathbf 0$ on $\\{u=t\\}$, the normal speed of the boundary is $1/|\\nabla u|$. For $g$ independent of $t$, Reynolds' theorem reproduces",
    options: [
      "Fubini's theorem",
      "The Coarea derivative formula $\\dfrac{d}{dt}\\displaystyle\\iint_{u<t}g\\,dA=\\int_{u=t}\\frac{g}{|\\nabla u|}\\,ds$",
      "Green's theorem",
      "The continuity equation",
    ],
    answer: "B",
    explanation: "$\\partial_tg=0$, so only the boundary term $\\int_{u=t}g\\cdot\\frac{1}{|\\nabla u|}\\,ds$ remains — the result from the Coarea topic.",
    id: "md-reynolds-18",
    difficulty: "Hard",
  },
  {
    prompt: "A circle of radius $1$ moves rigidly with velocity $(1,0)$, and $f=x$. The rate $\\dfrac{d}{dt}\\displaystyle\\iint_{D_t}x\\,dA$ is",
    options: [
      "$0$",
      "$\\pi$",
      "$2\\pi$",
      "$\\pi/2$",
    ],
    answer: "B",
    explanation: "For a rigid translation the rate is $\\iint\\mathbf c\\cdot\\nabla f\\,dA=\\iint1\\,dA=\\pi$. Directly: $\\iint_{D_t}x\\,dA=\\pi\\cdot(\\text{x-coordinate of the centre})=\\pi(x_0+t)$, whose derivative is $\\pi$.",
    id: "md-reynolds-19",
    difficulty: "Hard",
  },
  {
    prompt: "Which hypothesis is needed for the Reynolds Transport Theorem as stated in this guide?",
    options: [
      "The region must be a ball",
      "The velocity field must be divergence-free",
      "$f$ must be independent of time",
      "The region moves by a smooth, invertible flow, and $f$ and $\\partial f/\\partial t$ are continuous",
    ],
    answer: "D",
    explanation: "The proof pulls the integral back to a fixed region using the flow map and its Jacobian, which requires the flow to be smooth and invertible. Divergence-free fields and time-independent $f$ are special cases.",
    id: "md-reynolds-20",
    difficulty: "Hard",
  },
];
